// Shared, asset-free game illustrations (pure SVG/CSS + Framer Motion).
// Used by the trainee Arcade lobby AND the public landing page so visitors
// see the REAL game visuals — not generic icons.
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

export function Pin({ color, tx, ty }) {
    return (
        <div className="absolute" style={{ left: tx, top: ty, width: '24%', height: '32%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-full h-full rounded-full" style={{
                background: `radial-gradient(circle at 30% 25%, #fff 0%, ${color} 40%, ${color}dd 100%)`,
                boxShadow: '0 2px 3px rgba(0,0,0,0.4), inset 0 -2px 2px rgba(0,0,0,0.4)',
                border: '1px solid rgba(0,0,0,0.4)',
            }} />
        </div>
    );
}

export function LudoIllustration() {
    return (
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 p-3">
            <div className="rounded-md bg-emerald-500/80 ring-2 ring-emerald-300/60 relative">
                <Pin color="#10b981" tx="20%" ty="20%" />
                <Pin color="#10b981" tx="65%" ty="20%" />
                <Pin color="#10b981" tx="20%" ty="65%" />
                <Pin color="#10b981" tx="65%" ty="65%" />
            </div>
            <div className="bg-white/15" />
            <div className="rounded-md bg-rose-500/80 ring-2 ring-rose-300/60 relative">
                <Pin color="#fb7185" tx="20%" ty="20%" />
                <Pin color="#fb7185" tx="65%" ty="20%" />
                <Pin color="#fb7185" tx="20%" ty="65%" />
                <Pin color="#fb7185" tx="65%" ty="65%" />
            </div>
            <div className="bg-white/15" />
            <div className="bg-gradient-to-br from-yellow-300 via-emerald-400 via-rose-400 to-sky-400 rotate-45 rounded-sm" />
            <div className="bg-white/15" />
            <div className="rounded-md bg-yellow-400/90 ring-2 ring-yellow-200/70 relative">
                <Pin color="#FFF200" tx="20%" ty="20%" />
                <Pin color="#FFF200" tx="65%" ty="20%" />
                <Pin color="#FFF200" tx="20%" ty="65%" />
                <Pin color="#FFF200" tx="65%" ty="65%" />
            </div>
            <div className="bg-white/15" />
            <div className="rounded-md bg-sky-500/80 ring-2 ring-sky-300/60 relative">
                <Pin color="#3b82f6" tx="20%" ty="20%" />
                <Pin color="#3b82f6" tx="65%" ty="20%" />
                <Pin color="#3b82f6" tx="20%" ty="65%" />
                <Pin color="#3b82f6" tx="65%" ty="65%" />
            </div>
        </div>
    );
}

export function SnakeLadderIllustration() {
    return (
        <div className="absolute inset-0 p-3">
            <div className="grid grid-cols-5 grid-rows-5 w-full h-full gap-px">
                {Array.from({ length: 25 }).map((_, i) => (
                    <div key={i} className={`rounded-sm ${i % 2 === 0 ? 'bg-white/12' : 'bg-black/15'}`} />
                ))}
            </div>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-3 w-[calc(100%-1.5rem)] h-[calc(100%-1.5rem)] pointer-events-none">
                <g stroke="#fde047" strokeWidth="1.2" strokeLinecap="round">
                    <line x1="80" y1="85" x2="30" y2="20" />
                    <line x1="88" y1="80" x2="38" y2="15" />
                    <line x1="78" y1="73" x2="36" y2="73" strokeWidth="0.8" />
                    <line x1="66" y1="60" x2="34" y2="60" strokeWidth="0.8" />
                    <line x1="54" y1="46" x2="32" y2="46" strokeWidth="0.8" />
                    <line x1="44" y1="34" x2="30" y2="34" strokeWidth="0.8" />
                </g>
                <path d="M 18 18 Q 50 50 78 35 T 22 78" stroke="#fb7185" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <circle cx="18" cy="18" r="2.2" fill="#fb7185" stroke="#7f1d1d" strokeWidth="0.3" />
                <circle cx="17" cy="17" r="0.5" fill="#fff" />
                <circle cx="19" cy="17" r="0.5" fill="#fff" />
            </svg>
        </div>
    );
}

export function SpinWheelIllustration() {
    return (
        <div className="absolute inset-0 flex items-center justify-center">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 8, ease: 'linear' }}
                className="w-32 h-32 rounded-full relative" style={{
                    background: 'conic-gradient(#FFF200 0deg 45deg, #FFFFFF 45deg 90deg, #E31E24 90deg 135deg, #FFFFFF 135deg 180deg, #FFF200 180deg 225deg, #FFFFFF 225deg 270deg, #E31E24 270deg 315deg, #FFFFFF 315deg 360deg)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4), inset 0 0 0 4px rgba(255,255,255,0.5)',
                }}>
                <div className="absolute inset-0 rounded-full" style={{
                    background: 'radial-gradient(circle at 50% 50%, transparent 30%, rgba(0,0,0,0.2) 100%)'
                }} />
                <div className="absolute top-1/2 left-1/2 w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-900 ring-2 ring-white/70" />
            </motion.div>
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-0 h-0"
                style={{ borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '14px solid #fbbf24' }} />
        </div>
    );
}

export function MemoryMatchIllustration() {
    return (
        <div className="absolute inset-0 grid grid-cols-3 gap-1.5 p-4">
            {[
                'bg-rose-400', 'bg-yellow-400', 'bg-emerald-400',
                'bg-emerald-400', 'bg-sky-400', 'bg-violet-400',
                'bg-yellow-400', 'bg-violet-400', 'bg-rose-400',
            ].map((c, i) => (
                <motion.div key={i}
                    animate={{ rotateY: i === 4 ? 0 : [0, 180, 180, 0], y: [0, -2, 0] }}
                    transition={{ repeat: Infinity, duration: 4, delay: i * 0.15 }}
                    className={`rounded-lg ${i === 4 ? 'bg-white/95' : c} shadow-lg ring-1 ring-black/20`}
                    style={{ transformStyle: 'preserve-3d' }} />
            ))}
        </div>
    );
}

export function QuizBattleIllustration() {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 gap-2">
            <div className="bg-white/95 rounded-2xl px-3 py-2 text-slate-900 text-xs font-bold shadow-lg w-4/5">
                <div className="opacity-60 text-[8px] uppercase tracking-wider">Question</div>
                <div>What is FRLS?</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5 w-4/5">
                <div className="bg-emerald-500 rounded-xl px-2 py-1.5 text-white text-[10px] font-bold shadow ring-1 ring-emerald-300">A · Yes</div>
                <div className="bg-white/30 rounded-xl px-2 py-1.5 text-white text-[10px] font-bold">B</div>
                <div className="bg-white/30 rounded-xl px-2 py-1.5 text-white text-[10px] font-bold">C</div>
                <div className="bg-white/30 rounded-xl px-2 py-1.5 text-white text-[10px] font-bold">D</div>
            </div>
        </div>
    );
}

export function ScratchCardIllustration() {
    return (
        <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-36 h-24 rounded-xl bg-gradient-to-br from-slate-300 to-slate-500 overflow-hidden shadow-xl ring-2 ring-white/30">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500 flex items-center justify-center">
                    <div className="text-slate-900 font-display font-extrabold text-base">+50 pts</div>
                </div>
                <motion.div
                    animate={{ x: [-40, 50] }} transition={{ repeat: Infinity, duration: 3, repeatType: 'mirror' }}
                    className="absolute inset-0 bg-gradient-to-br from-slate-400 to-slate-600"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 40% 70%, 30% 40%, 60% 30%, 50% 10%, 30% 25%, 10% 15%, 5% 60%)' }} />
                <Sparkles className="absolute top-1 right-2 h-4 w-4 text-white animate-pulse" />
            </div>
        </div>
    );
}

export function FlashcardIllustration() {
    return (
        <div className="absolute inset-0 flex items-center justify-center perspective-[600px]">
            <motion.div
                animate={{ rotateY: [0, 180, 180, 0] }}
                transition={{ repeat: Infinity, duration: 4, times: [0, 0.45, 0.55, 1] }}
                style={{ transformStyle: 'preserve-3d' }}
                className="relative w-36 h-24 rounded-2xl shadow-xl"
            >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white to-slate-100 ring-2 ring-white/40 flex items-center justify-center"
                    style={{ backfaceVisibility: 'hidden' }}>
                    <div className="text-slate-900 font-display font-extrabold">FR</div>
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 ring-2 ring-emerald-200 flex items-center justify-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                    <div className="text-white font-display font-extrabold text-xs px-2 text-center">Fire Retardant</div>
                </div>
            </motion.div>
        </div>
    );
}

export function KBCIllustration() {
    return (
        <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0" style={{
                background: 'radial-gradient(ellipse at center, rgba(120,53,15,0.55) 0%, rgba(15,23,42,0.95) 70%)',
            }} />
            <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.55, 0.85, 0.55] }}
                transition={{ repeat: Infinity, duration: 3.4, ease: 'easeInOut' }}
                className="absolute left-1/2 top-1/2 w-44 h-44 -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl"
                style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.65) 0%, transparent 60%)' }}
            />
            <div className="absolute top-3 bottom-3 right-3 w-12 sm:w-14 flex flex-col-reverse gap-[3px]">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className={`flex-1 rounded-sm ${i === 3 ? 'bg-gradient-to-r from-amber-300 to-amber-500 shadow-[0_0_8px_rgba(251,191,36,0.85)]' : 'bg-slate-700/60'}`} />
                ))}
            </div>
            <motion.div
                initial={{ scale: 0.9, opacity: 0.85 }}
                animate={{ scale: [0.95, 1.02, 0.95] }}
                transition={{ repeat: Infinity, duration: 2.6, ease: 'easeInOut' }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-[60%] rounded-xl p-2"
                style={{
                    background: 'linear-gradient(135deg, #0a1a4a 0%, #06112e 80%)',
                    border: '1.5px solid rgba(251,191,36,0.5)',
                    boxShadow: '0 0 22px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
            >
                <div className="text-[8px] uppercase tracking-widest text-amber-300 font-bold text-center">For 1 Crore Pts</div>
                <div className="mt-1 grid grid-cols-2 gap-1">
                    {['A', 'B', 'C', 'D'].map((l, i) => (
                        <div key={l}
                             className={`text-[9px] sm:text-[10px] font-display font-extrabold px-1.5 py-0.5 rounded text-center ${
                                 i === 1
                                     ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 shadow-[0_0_8px_rgba(251,191,36,0.85)]'
                                     : 'bg-slate-800/70 text-slate-300 ring-1 ring-slate-700'
                             }`}
                             style={i === 1 ? { transform: 'scale(1.05)' } : {}}>
                            {l}
                        </div>
                    ))}
                </div>
            </motion.div>
            <motion.div
                animate={{ y: [0, -3, 0] }}
                transition={{ repeat: Infinity, duration: 1.2 }}
                className="absolute bottom-2 left-3 text-amber-300 font-display font-extrabold text-[10px] uppercase tracking-widest"
                style={{ textShadow: '0 0 10px rgba(251,191,36,0.8)' }}
            >
                Lock kiya jaye?
            </motion.div>
        </div>
    );
}

export function PulsePowerIllustration() {
    const rows = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    return (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(255,242,0,0.6), transparent 60%)' }} />
            <div className="relative flex flex-col-reverse gap-[3px] w-2/3">
                {rows.map((v, i) => (
                    <motion.div
                        key={v}
                        initial={{ scaleX: 0.3, opacity: 0.4 }}
                        animate={{ scaleX: 1, opacity: i === 4 ? 1 : 0.85 }}
                        transition={{ delay: i * 0.08, repeat: Infinity, repeatType: 'reverse', duration: 1.6 }}
                        className={`origin-left h-3 rounded-full flex items-center justify-between px-2 text-[8px] font-extrabold ${i === 4 ? 'bg-brand-yellow text-slate-900' : 'bg-white/15 text-white'}`}
                    >
                        <span>{String.fromCharCode(65 + (9 - i))}</span>
                        <span>+{v}</span>
                    </motion.div>
                ))}
            </div>
            <Sparkles className="absolute top-4 right-5 h-6 w-6 text-yellow-200/80" />
        </div>
    );
}


export function PulseCrushIllustration() {
    const colors = ['#F5C542', '#3B82F6', '#EF4444', '#FBBF24', '#22C55E'];
    const grid = Array.from({ length: 16 });
    return (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 50% 20%, rgba(245,197,66,0.6), transparent 60%)' }} />
            <div className="relative grid grid-cols-4 gap-1.5 w-2/5">
                {grid.map((_, i) => (
                    <motion.div key={i}
                        initial={{ scale: 0.6, opacity: 0.4 }}
                        animate={{ scale: [0.7, 1, 0.7], opacity: [0.6, 1, 0.6] }}
                        transition={{ delay: (i % 4) * 0.12 + Math.floor(i / 4) * 0.08, repeat: Infinity, duration: 1.8 }}
                        className="aspect-square rounded-md"
                        style={{ background: colors[(i * 3 + Math.floor(i / 4)) % colors.length], boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.35)' }}
                    />
                ))}
            </div>
            <Sparkles className="absolute top-4 right-5 h-6 w-6 text-yellow-200/80" />
        </div>
    );
}


export const ILLUSTRATIONS = {
    pulse_power_quiz: { Render: PulsePowerIllustration, bg: 'from-yellow-600 via-amber-700 to-slate-900', tag: 'PULSE POWER', accent: 'text-yellow-200' },
    pulse_crush:   { Render: PulseCrushIllustration, bg: 'from-amber-600 via-yellow-800 to-slate-900', tag: 'MATCH-3', accent: 'text-yellow-200' },
    kbc_learning:  { Render: KBCIllustration, bg: 'from-amber-900 via-slate-900 to-black', tag: 'HOT SEAT', accent: 'text-amber-300' },
    ludo_learning: { Render: LudoIllustration, bg: 'from-violet-700 via-indigo-800 to-slate-900', tag: 'BOARD GAME', accent: 'text-amber-300' },
    snake_ladder:  { Render: SnakeLadderIllustration, bg: 'from-emerald-700 via-teal-800 to-slate-900', tag: 'CLASSIC', accent: 'text-emerald-200' },
    spin_wheel:    { Render: SpinWheelIllustration, bg: 'from-amber-500 via-orange-600 to-rose-700', tag: 'LUCKY SPIN', accent: 'text-yellow-100' },
    memory_match:  { Render: MemoryMatchIllustration, bg: 'from-rose-600 via-fuchsia-700 to-purple-800', tag: 'BRAIN GAME', accent: 'text-rose-100' },
    quiz_battle:   { Render: QuizBattleIllustration, bg: 'from-sky-600 via-blue-700 to-indigo-800', tag: 'TRIVIA', accent: 'text-sky-100' },
    scratch_card:  { Render: ScratchCardIllustration, bg: 'from-slate-700 via-zinc-800 to-slate-900', tag: 'INSTANT WIN', accent: 'text-amber-200' },
    flashcard:     { Render: FlashcardIllustration, bg: 'from-teal-600 via-emerald-700 to-green-800', tag: 'STUDY', accent: 'text-emerald-100' },
};

// Featured-first display order
export const ORDER = ['pulse_power_quiz', 'pulse_crush', 'kbc_learning', 'ludo_learning', 'snake_ladder', 'spin_wheel', 'quiz_battle', 'memory_match', 'flashcard', 'scratch_card'];
